import {
  Box,
  Grid,
  Paper,
  TableContainer,
  Typography,
  Button,
  Stack,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import ExcelJS from 'exceljs';
import DataTable from './DataGrid';
import { services } from '../pages/Sell/services';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useDialogContext } from './dialog-context';
import DateRangeDialog from './excel-output-dialog';
import PdfExportDialog from './pdf-dxport-dialog'; // Keep your existing import path

const SaleTransactions = ({ trigger, setTotalTodaysSell }) => {
  const [rows, setRows] = useState([]);
  const { openDialog, closeDialog } = useDialogContext();
  const [isPdfDialogOpen, setIsPdfDialogOpen] = useState(false);

  useEffect(() => {
    services.getInvoiceList().then(data => {
      // Depending on your axios setup, it might be data or data.data
      const results = data.data || data;
      results.sort(compare);
      setRows(results);
    });
  }, [trigger]);

  // Helper to convert Persian date string or Date object to Excel's date number (optional)
  const generateExcelStyled = async data => {
    if (!data.length) {
      alert('هیچ تراکنشی در بازه زمانی انتخاب شده وجود ندارد');
      return;
    }

    const allTypes = [
      'GB_999_1',
      'GB_999_2',
      'GB_999_5',
      'GB_999_10',
      'GB_999_20',
      'GB_999_50',
      'GB_999_100',
    ];
    const sortedTypes = [...allTypes].sort((a, b) => {
      const weightA = parseInt(a.split('_').pop(), 10);
      const weightB = parseInt(b.split('_').pop(), 10);
      return weightA - weightB;
    });

    // Build headers
    const headers = [
      'تاریخ',
      'شماره فاکتور',
      'نام خریدار',
      'شماره ملی',
      'شماره تلفن همراه',
      'قیمت هر گرم (ریال)',
    ];
    sortedTypes.forEach(type => {
      const weight = type.split('_').pop();
      headers.push(`شمش ${weight} گرمی (عدد)`);
      headers.push(`قیمت (ریال)`);
    });
    headers.push('مجموع (ریال)');
    headers.push('شماره شعبه');

    // Create workbook and worksheet
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('تراکنش‌های فروش');

    worksheet.columns = headers.map(() => ({ width: 15 }));

    const headerRow = worksheet.addRow(headers);
    headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } }; // white text
    headerRow.height = 25;

    headerRow.eachCell((cell, colNumber) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF1E88E5' }, // blue background for all headers
      };
      cell.alignment = { horizontal: 'right', vertical: 'right' };
    });

    data.forEach(transaction => {
      const typeMap = new Map();
      transaction.items?.forEach(item => {
        typeMap.set(item.type, {
          count: item.count,
          price: item.price.value,
        });
      });
      let totalPrice = 0;
      let totalWeight = 0;
      for (let [bullion, { count, price }] of typeMap) {
        const weight = Number(bullion.slice(7));
        totalWeight += weight * Number(count);
        totalPrice += Number(count) * Number(price);
      }

      const pricePerGram = totalPrice / totalWeight;

      const persianDate = new Date(transaction.createdAt).toLocaleDateString(
        'fa-IR',
        {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        }
      );

      const rowData = [
        persianDate,
        transaction.id,
        transaction.partyName,
        transaction.partyNationalId,
        transaction.partyPhoneNumber || '',
        Math.trunc(pricePerGram),
      ];

      sortedTypes.forEach(type => {
        const info = typeMap.get(type);

        rowData.push(info ? Number(info.count) : 0);
        rowData.push(info ? Number(info.price) : 0);
      });
      rowData.push(totalPrice);
      rowData.push(transaction.branch);

      const row = worksheet.addRow(rowData);

      const colIndexToColor = [1, 2, 3, 4, 5, 6, 7, 9, 11, 13, 15, 17, 19, 21];

      row.eachCell((cell, colNumber) => {
        if (colIndexToColor.includes(colNumber)) {
          cell.font = { color: { argb: 'FF2C2C' } };
        }
        if (typeof cell.value === 'number') {
          cell.alignment = { horizontal: 'right' };
        } else {
          cell.alignment = { horizontal: 'right' };
        }
      });
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = `transactions_${new Date().toISOString().slice(0, 19)}.xlsx`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const fetchAndExport = async (fromDate, toDate) => {
    try {
      const from = fromDate.toISOString();
      const to = toDate.toISOString();
      const data = await services.getInvoiceList({ from, to });

      const results = data.data || data;
      results.sort(compare);
      await generateExcelStyled(results);
    } catch (error) {
      console.error('Export failed', error);
      alert('خطا در دریافت اطلاعات');
    }
  };

  const handleExportClick = () => {
    openDialog(
      'انتخاب بازه زمانی',
      <DateRangeDialog
        onConfirm={async (fromDate, toDate) => {
          await fetchAndExport(fromDate, toDate);
          closeDialog();
        }}
        onClose={closeDialog}
      />
    );
  };

  const handleFetchTransactions = async query => {
    try {
      const response = await services.getInvoiceList({ search: query });

      return response.data || response || [];
    } catch (error) {
      console.error('Error fetching exact transaction:', error);
      return [];
    }
  };

  const handlePdfConfirm = async params => {
    try {
      await services.getInvoicePdf(params);
    } catch (error) {
      console.error('Error fetching PDF data:', error);
      alert('خطا در دریافت فایل PDF');
    }
  };

  return (
    <Grid
      pt={'30px !important'}
      container
      item
      xs={12}
      spacing={{ xs: 2, sm: 2, md: 3 }}
    >
      <Grid mt={{ xs: '20px', md: '40px' }} pt={'0px !important'} item xs={12}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: { xs: 2 },
          }}
        >
          <Typography
            variant={{ xs: 'h6', sm: 'h6', md: 'h5' }}
            sx={{
              fontWeight: 700,
              color: '#111827',
              letterSpacing: '0.5px',
              textAlign: { xs: 'left', sm: 'center', md: 'center' },
            }}
          >
            تراکنش‌های فروش
          </Typography>
          <Stack direction="row" spacing={2} sx={{ mb: 0 }}>
            <Button
              variant="contained"
              // startIcon={<FileDownloadIcon />}
              onClick={handleExportClick}
              sx={{
                background: 'linear-gradient(45deg, #2dd4bf, #14b8a6)',
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1,
              }}
            >
              خروجی اکسل
            </Button>
            <Button
              variant="contained"
              // startIcon={<PictureAsPdfIcon />}
              onClick={() => setIsPdfDialogOpen(true)}
              sx={{
                backgroundColor: '#2563eb',
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1,
                '&:hover': { backgroundColor: '#1d4ed8' },
              }}
            >
              دریافت فاکتور فروش
            </Button>
          </Stack>

          <PdfExportDialog
            open={isPdfDialogOpen}
            onClose={() => setIsPdfDialogOpen(false)}
            onConfirm={handlePdfConfirm}
            fetchTransactions={handleFetchTransactions}
          />
        </Box>

        <TableContainer
          component={Paper}
          sx={{
            mt: 2,
            borderRadius: '12px',
            border: 'none',
            boxShadow: 'none',
            background: 'transparent',
            overflowX: { xs: 'auto', sm: 'auto', md: 'visible' },
            WebkitOverflowScrolling: 'touch',
            minWidth: 'auto',
          }}
        >
          <DataTable
            trigger={trigger}
            setTotalTodaysSell={setTotalTodaysSell}
            sx={{
              '& .MuiTableHead-root': {
                background: 'linear-gradient(45deg, #2563eb, #1d4ed8)',
                borderRadius: '12px 12px 0 0',
                '& th': {
                  fontWeight: 700,
                  color: '#fff',
                  py: { xs: 1.5, sm: 1.5, md: 2 },
                  px: { xs: 1, sm: 1.5, md: 2 },
                  fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.85rem' },
                  borderBottom: 'none',
                  whiteSpace: 'nowrap',
                },
              },
              '& .MuiTableBody-root': {
                '& tr': {
                  background: theme =>
                    theme.palette.mode === 'light'
                      ? 'rgba(255, 255, 255, 0.9)'
                      : 'rgba(243, 244, 246, 0.9)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'rgba(191, 219, 254, 0.7)',
                    boxShadow: '0 4px 16px rgba(59, 130, 246, 0.2)',
                  },
                  '& td': {
                    color: '#111827',
                    borderBottom: 'none',
                    fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.85rem' },
                    py: { xs: 1, sm: 1.2, md: 1.5 },
                    px: { xs: 1, sm: 1.5, md: 2 },
                    fontWeight: 500,
                    whiteSpace: { xs: 'nowrap', sm: 'nowrap', md: 'normal' },
                  },
                },
              },
            }}
          />
        </TableContainer>
      </Grid>
    </Grid>
  );
};

export default SaleTransactions;

function compare(a, b) {
  //sort both by date and id
  const dateA = new Date(a.createdAt).getTime();
  const dateB = new Date(b.createdAt).getTime();

  if (dateA < dateB) return -1;
  if (dateA > dateB) return 1;
  if (a.id < b.id) return -1;
  if (a.id > b.id) return 1;

  return 0;
}
